<script setup lang="ts">
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Input } from '@/components/ui/input'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { ROUTES } from '@/router/routes'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore } from '@/store/userStore'
  import type {
    AiChatQuestionTemplate,
    AiChatQuestionTemplateRequest,
    AIChatSession,
    CustomerCase
  } from '@/types/generated'
  import { AiChatQuestionTemplatesService, AIChatSessionTypeEnum } from '@/types/generated'
  import {
    AlertCircle,
    Check,
    ChevronLeft,
    Copy,
    LoaderCircle,
    MoreVertical,
    Pencil,
    Plus,
    RotateCw,
    Scale,
    Search,
    Star,
    Trash
  } from 'lucide-vue-next'
  import { computed, inject, onMounted, ref, type Ref } from 'vue'
  import { useRouter } from 'vue-router'

  // Define emit for closing the popover and prefilling the input
  const emit = defineEmits(['close', 'prefill'])

  // Router for navigation
  const router = useRouter()

  const { t } = useTranslation({
    en: {
      title: 'Prompt Templates',
      search: 'Search',
      newTemplate: 'New Template',
      favorites: 'Favorites',
      savedTemplates: 'Saved templates',
      all: 'All templates',
      allgemein: 'General',
      arbeitsrecht: 'Labor Law',
      insolvenzrecht: 'Insolvency Law',
      mietrecht: 'Tenancy Law',
      strafrecht: 'Criminal Law',
      kündigung: 'Termination',
      noTemplates: 'No templates available',
      manageTemplates: 'Manage templates',
      use: 'Use',
      edit: 'Edit',
      duplicate: 'Duplicate',
      delete: 'Delete',
      confirmDelete: 'Confirm delete',
      deleteTemplateQuestion: 'Are you sure you want to delete this template?',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      templateTitle: 'Template Title',
      templateTitlePlaceholder: 'Enter a title for this template',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      prompt: 'Prompt',
      promptPlaceholder: 'Enter the prompt text that should be used as a template',
      promptRequired: 'Prompt is required',
      description: 'Description',
      descriptionPlaceholder: 'Enter a description for this template (max 300 characters)',
      descriptionTooLong: 'Description is too long (max 300 characters)',
      generateWithAI: 'Generate with AI',
      generatingDescription: 'Generating description...',
      selectLawField: 'Select law field...',
      selectLawTopic: 'Select law topic...',
      characters: 'characters',
      templateVisibility: 'This template will be visible to all users in your lawfirm',
      createTemplate: 'Create Template',
      editTemplate: 'Edit Template',
      duplicateTemplate: 'Duplicate Template',
      backToAllTemplates: 'Back to all templates',
      saveChanges: 'Save changes',
      saved: 'Saved',
      saveFailed: 'Save failed',
      untitledTemplate: 'New Template',
      noLawFieldSelected: 'No law field selected',
      jupus: 'Jupus',
      jupusTemplateTooltip:
        'Jupus templates are predefined templates that cannot be edited but you can duplicate them and change them as you wish. They come preinstalled with the system.',
      folders: 'Folders',
      noFolders: 'No folders available'
    },
    de: {
      title: 'Promptvorlagen',
      search: 'Suchen',
      newTemplate: 'Neue Promptvorlage',
      favorites: 'Favoriten',
      savedTemplates: 'Gespeicherte Vorlagen',
      all: 'Alle Vorlagen',
      allgemein: 'Allgemein',
      arbeitsrecht: 'Arbeitsrecht',
      insolvenzrecht: 'Insolvenzrecht',
      mietrecht: 'Mietrecht',
      strafrecht: 'Strafrecht',
      kündigung: 'Kündigung',
      noTemplates: 'Keine Vorlagen verfügbar',
      manageTemplates: 'Vorlagen verwalten',
      use: 'Verwenden',
      edit: 'Bearbeiten',
      duplicate: 'Duplizieren',
      delete: 'Löschen',
      confirmDelete: 'Löschen bestätigen',
      deleteTemplateQuestion: 'Sind Sie sicher, dass Sie diese Vorlage löschen möchten?',
      cancel: 'Abbrechen',
      save: 'Speichern',
      saving: 'Speichern...',
      templateTitle: 'Name',
      templateTitlePlaceholder: 'Geben Sie einen Namen für diese Vorlage ein',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      prompt: 'Prompt',
      promptPlaceholder: 'Geben Sie den Prompttext ein, der als Vorlage verwendet werden soll',
      promptRequired: 'Prompt ist erforderlich',
      description: 'Beschreibung',
      descriptionPlaceholder:
        'Geben Sie eine Beschreibung für diese Vorlage ein (max. 300 Zeichen)',
      descriptionTooLong: 'Beschreibung ist zu lang (max. 300 Zeichen)',
      generateWithAI: 'Mit KI generieren',
      generatingDescription: 'Beschreibung wird generiert...',
      selectLawField: 'Rechtsgebiet auswählen...',
      selectLawTopic: 'Rechtsthema auswählen...',
      characters: 'Zeichen',
      templateVisibility: 'Diese Vorlage wird für alle Benutzer in Ihrer Kanzlei sichtbar sein',
      createTemplate: 'Vorlage erstellen',
      editTemplate: 'Vorlage bearbeiten',
      duplicateTemplate: 'Vorlage duplizieren',
      backToAllTemplates: 'Zurück zu allen Vorlagen',
      saveChanges: 'Änderungen speichern',
      saved: 'Gespeichert',
      saveFailed: 'Speichern fehlgeschlagen',
      untitledTemplate: 'New Template',
      noLawFieldSelected: 'Kein Rechtsgebiet ausgewählt',
      jupus: 'Jupus',
      jupusTemplateTooltip:
        'Jupus-Vorlagen sind vordefinierte Vorlagen, die nicht bearbeitet werden können, aber Sie können sie duplizieren und nach Ihren Wünschen ändern. Sie sind mit dem System vorinstalliert.',
      folders: 'Ordner',
      noFolders: 'Keine Ordner verfügbar'
    }
  })

  const { translateLawField } = useLawFieldTranslation()
  const { translateLawTopic } = useLawTopicTranslation()

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))

  const globalChatStore = useGlobalChatStore()
  const userStore = useUserStore()
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })

  // Fetch templates from API
  const fetchTemplates = async () => {
    try {
      const response = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesList({
        limit: 0
      })

      // Update the templates in the appropriate store
      if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
        globalChatStore.promptTemplates = response.results
      } else if (caseData?.value?.org) {
        caseData.value.org.ai_chat_question_templates = response.results
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  }

  // Search functionality
  const searchQuery = ref('')

  // Active category
  const activeCategory = ref('all')

  // Compute unique law fields and their counts
  const lawFieldCategories = computed(() => {
    // For global context, use global prompt templates
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      const templates = globalChatStore.promptTemplates || []
      const lawFields: Record<string, number> = {}

      templates.forEach((template) => {
        if (template.law_field) {
          lawFields[template.law_field] = (lawFields[template.law_field] || 0) + 1
        }
      })

      return Object.entries(lawFields)
        .map(([name, count]) => ({ name, count: count as number }))
        .sort((a, b) => a.name.localeCompare(b.name))
    }

    // For case context, use case templates
    if (!caseData.value?.org?.ai_chat_question_templates) return []

    const templates = caseData.value.org.ai_chat_question_templates
    const lawFields = {}

    // Count templates for each law field
    templates.forEach((template) => {
      if (template.law_field) {
        if (!lawFields[template.law_field]) {
          lawFields[template.law_field] = 0
        }
        lawFields[template.law_field]++
      }
    })

    // Convert to array for rendering
    return Object.entries(lawFields)
      .map(([field, count]) => ({
        name: field,
        count
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  // Count for special categories
  const specialCategoryCounts = computed(() => {
    // For global context, use global prompt templates
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      const templates = globalChatStore.promptTemplates || []
      return {
        all: templates.length,
        jupus: templates.filter((t) => t.is_jupus_template).length,
        saved: templates.filter((t) => !t.is_jupus_template).length,
        favorites: templates.filter((t) => t.is_favorite).length
      }
    }

    // For case context, use case templates
    if (!caseData.value?.org?.ai_chat_question_templates) {
      return { all: 0, jupus: 0, saved: 0, favorites: 0 }
    }

    const templates = caseData.value.org.ai_chat_question_templates
    return {
      all: templates.length,
      jupus: templates.filter((t) => t.is_jupus_template).length,
      saved: templates.filter((t) => !t.is_jupus_template).length,
      favorites: templates.filter((t) => t.is_favorite).length
    }
  })

  // Filtered templates based on search and category
  const filteredTemplates = computed(() => {
    // For global context, use global prompt templates
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      let templates = globalChatStore.promptTemplates || []

      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        templates = templates.filter(
          (template) =>
            template.title.toLowerCase().includes(query) ||
            template.prompt.toLowerCase().includes(query) ||
            template.law_field?.toLowerCase().includes(query) ||
            (template.law_topic && template.law_topic.toLowerCase().includes(query))
        )
      }

      // Filter by category
      if (activeCategory.value === 'jupus') {
        templates = templates.filter((template) => template.is_jupus_template)
      } else if (activeCategory.value === 'saved') {
        templates = templates.filter((template) => !template.is_jupus_template)
      } else if (activeCategory.value === 'favorites') {
        templates = templates.filter((template) => template.is_favorite)
      } else if (activeCategory.value !== 'all') {
        templates = templates.filter((template) => template.law_field === activeCategory.value)
      }

      return templates
    }

    // For case context, use case templates
    if (!caseData.value?.org?.ai_chat_question_templates) return []

    let templates = caseData.value.org.ai_chat_question_templates

    // Filter by search query
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      templates = templates.filter(
        (template) =>
          template.title.toLowerCase().includes(query) ||
          template.prompt.toLowerCase().includes(query) ||
          template.law_field?.toLowerCase().includes(query) ||
          (template.law_topic && template.law_topic.toLowerCase().includes(query))
      )
    }

    // Filter by category
    if (activeCategory.value === 'jupus') {
      templates = templates.filter((template) => template.is_jupus_template)
    } else if (activeCategory.value === 'saved') {
      templates = templates.filter((template) => !template.is_jupus_template)
    } else if (activeCategory.value === 'favorites') {
      templates = templates.filter((template) => template.is_favorite)
    } else if (activeCategory.value !== 'all') {
      templates = templates.filter((template) => template.law_field === activeCategory.value)
    }

    return templates
  })

  // Get truncated text preview (for prompt or description)
  function getPromptPreview(text: string, maxLength = 120) {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  // Handle selecting a template
  const selectTemplate = (template: AiChatQuestionTemplate) => {
    if (!isActiveSessionGenerating.value) {
      // Emit an event with the template content to prefill the chat input
      emit('prefill', template.prompt)
      // Close the popover after selecting
      emit('close')
    }
  }

  // Toggle favorite status for a template
  const toggleFavorite = async (template: AiChatQuestionTemplate) => {
    try {
      if (template.is_favorite) {
        // Update the template in the local state
        if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
          const index = globalChatStore.promptTemplates.findIndex((t) => t.uuid === template.uuid)
          if (index !== -1) {
            globalChatStore.promptTemplates[index] = {
              ...globalChatStore.promptTemplates[index],
              is_favorite: false
            }
          }
        } else {
          const list = caseData.value?.org?.ai_chat_question_templates
          const index = list?.findIndex((t) => t.uuid === template.uuid) ?? -1
          if (index !== -1 && caseData.value?.org?.ai_chat_question_templates) {
            caseData.value.org.ai_chat_question_templates[index] = {
              ...caseData.value.org.ai_chat_question_templates[index],
              is_favorite: false
            }
          }
        }
        // Unfavorite the template
        const response =
          await AiChatQuestionTemplatesService.aiChatQuestionTemplatesUnfavoriteCreate({
            uuid: template.uuid,
            requestBody: {}
          })
      } else {
        // Update the template in the local state
        if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
          const index = globalChatStore.promptTemplates.findIndex((t) => t.uuid === template.uuid)
          if (index !== -1) {
            globalChatStore.promptTemplates[index] = {
              ...globalChatStore.promptTemplates[index],
              is_favorite: true
            }
          }
        } else {
          const list = caseData.value?.org?.ai_chat_question_templates
          const index = list?.findIndex((t) => t.uuid === template.uuid) ?? -1
          if (index !== -1 && caseData.value?.org?.ai_chat_question_templates) {
            caseData.value.org.ai_chat_question_templates[index] = {
              ...caseData.value.org.ai_chat_question_templates[index],
              is_favorite: true
            }
          }
        }

        // Favorite the template
        const response = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesFavoriteCreate(
          {
            uuid: template.uuid,
            requestBody: {}
          }
        )
      }
    } catch (error) {
      console.error('Failed to toggle favorite status:', error)
    }
  }

  // Navigate to template settings page
  const goToTemplateSettings = async () => {
    await router.push({
      path: ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATES,
      replace: true
    })
    emit('close')
  }

  // Create new template function is defined below with the other template form functions

  // Set active category
  const setActiveCategory = (category: string) => {
    activeCategory.value = category
  }

  // Template actions
  const editTemplate = (template: AiChatQuestionTemplate) => {
    // Prevent editing Jupus templates
    if (template.is_jupus_template) return
    openTemplateForm('edit', template)
  }

  const duplicateTemplate = (template: AiChatQuestionTemplate) => {
    openTemplateForm('duplicate', template)
  }

  const createNewTemplate = () => {
    openTemplateForm('create')
  }

  // Constants
  const TEMPLATE_TITLE_MAX_LENGTH = 50

  // Template form state
  const showTemplateForm = ref(false)
  const templateFormMode = ref<'create' | 'edit' | 'duplicate'>('create')
  const selectedTemplate = ref<AiChatQuestionTemplate | null>(null)
  const currentView = ref<'grid' | 'form'>('grid')
  const templateTitle = ref('')
  const promptText = ref('')
  const descriptionText = ref('')
  const isGeneratingDescription = ref(false)
  const selectedLawField = ref('')
  const selectedLawTopic = ref('')
  const isSaving = ref(false)
  const isDataLoaded = ref(false)
  const DESCRIPTION_MAX_LENGTH = 300
  const selectedFolderUuids = ref<string[]>([])

  // Law field store access
  const { fetchLawFields, getLawTopicOptions, getLawFieldOptions } = useLawFieldStore()

  // Generate law field options from user store for both global and case contexts
  const lawFieldOptions = computed<Option[]>(() => {
    // For global context, use user store
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      if (!userStore.user) return []
      return getLawFieldOptions(userStore.user)
    }

    // For case context, prefer case data, fallback to user store
    if (caseData.value?.org?.law_fields) {
      return (caseData.value.org.law_fields as string[]).map((field) => ({
        value: field,
        title: field
      }))
    }

    // Fallback to user store
    if (!userStore.user) return []
    return getLawFieldOptions(userStore.user)
  })

  const lawTopicOptions = computed<Option[]>(() => {
    if (!selectedLawField.value) return []
    return getLawTopicOptions(selectedLawField.value)
  })

  const isTemplateTitleValid = computed(() => {
    return templateTitle.value.length > 0 && templateTitle.value.length <= 50
  })

  const isPromptValid = computed(() => {
    return promptText.value.length > 0
  })

  const isDescriptionValid = computed(() => {
    return (
      descriptionText.value.length <= DESCRIPTION_MAX_LENGTH && descriptionText.value.length > 0
    )
  })

  const isFormValid = computed(() => {
    return isTemplateTitleValid.value && isPromptValid.value && isDescriptionValid.value
  })

  // Load law fields data and ensure user data is loaded
  onMounted(async () => {
    try {
      // Ensure user data is loaded
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      // Load law fields data
      await fetchLawFields()

      // Load session groups (folders) for the folder assignment picker
      if (!globalChatStore.sessionGroups.length) {
        await globalChatStore.loadSessionGroups()
      }

      isDataLoaded.value = true
    } catch (error) {
      console.error('Failed to load data:', error)
      // Set data loaded to true even on error to prevent blocking the UI
      isDataLoaded.value = true
    }
  })

  function handleLawFieldChange(value: string) {
    selectedLawTopic.value = ''
  }

  async function generateDescription() {
    if (!promptText.value) return

    isGeneratingDescription.value = true
    descriptionText.value = ''

    try {
      const response =
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesGenerateDescriptionCreate({
          requestBody: {
            prompt: promptText.value,
            law_field: selectedLawField.value,
            law_topic: selectedLawTopic.value
          }
        })

      // Simulate streaming effect for better UX
      const description = response.description
      let currentIndex = 0

      // Clear any previous content
      descriptionText.value = ''

      // Stream the description character by character
      const streamInterval = setInterval(() => {
        if (currentIndex < description.length) {
          descriptionText.value += description[currentIndex]
          currentIndex++
        } else {
          clearInterval(streamInterval)
          isGeneratingDescription.value = false
        }
      }, 15) // Adjust speed as needed
    } catch (error) {
      console.error('Failed to generate description:', error)
      isGeneratingDescription.value = false
    }
  }

  function openTemplateForm(
    mode: 'create' | 'edit' | 'duplicate',
    template?: AiChatQuestionTemplate
  ) {
    templateFormMode.value = mode
    selectedTemplate.value = template || null

    if (template && (mode === 'edit' || mode === 'duplicate')) {
      templateTitle.value = mode === 'duplicate' ? `${template.title} (Copy)` : template.title
      promptText.value = template.prompt
      descriptionText.value = template.description || ''
      selectedLawField.value = template.law_field
      selectedLawTopic.value = template.law_topic || ''
      selectedFolderUuids.value = mode === 'edit' ? (template as any).folders || [] : []
    } else {
      // New template
      templateTitle.value = ''
      promptText.value = ''
      descriptionText.value = ''
      selectedLawField.value = ''
      selectedLawTopic.value = ''
      selectedFolderUuids.value = []
    }

    // Switch to form view
    currentView.value = 'form'
  }

  function closeTemplateForm() {
    currentView.value = 'grid'
    resetTemplateForm()
  }

  function resetTemplateForm() {
    templateTitle.value = ''
    promptText.value = ''
    descriptionText.value = ''
    selectedLawField.value = ''
    selectedLawTopic.value = ''
    selectedFolderUuids.value = []
    selectedTemplate.value = null
  }

  // Save success state
  const saveSuccess = ref(false)
  const saveError = ref(false)

  async function saveTemplate() {
    if (!isFormValid.value) return

    // Prevent saving changes to Jupus templates
    if (selectedTemplate.value?.is_jupus_template && templateFormMode.value === 'edit') {
      saveError.value = true
      setTimeout(() => {
        saveError.value = false
      }, 2500)
      return
    }

    isSaving.value = true
    saveSuccess.value = false
    saveError.value = false

    try {
      // Create the base request body
      const requestBody: AiChatQuestionTemplateRequest & { folders?: string[] } = {
        title: templateTitle.value,
        prompt: promptText.value,
        description: descriptionText.value,
        folders: selectedFolderUuids.value
      }

      // Only include law_field if it's not empty
      if (selectedLawField.value) {
        requestBody.law_field = selectedLawField.value
      }

      // Only include law_topic if it's not empty
      if (selectedLawTopic.value) {
        requestBody.law_topic = selectedLawTopic.value
      }

      // Ensure loading state is visible for at least 1 second
      const startTime = Date.now()

      if (templateFormMode.value === 'edit' && selectedTemplate.value) {
        // Update existing template
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesPartialUpdate({
          uuid: selectedTemplate.value.uuid,
          requestBody
        })
      } else {
        // Create new template or duplicate
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesCreate({
          requestBody
        })
      }

      // Calculate how much time has passed and wait if needed
      const elapsedTime = Date.now() - startTime
      const minLoadingTime = 1000 // 1 second minimum loading time

      if (elapsedTime < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime))
      }

      // Show success state temporarily
      saveSuccess.value = true

      // Keep success state visible for 2 seconds
      setTimeout(() => {
        saveSuccess.value = false
      }, 2000)

      // Refresh the templates list
      await fetchTemplates()

      // Only close the form on create/duplicate, stay in edit mode for edits
      if (templateFormMode.value !== 'edit') {
        closeTemplateForm()
      }
    } catch (error) {
      // Ensure error state isn't shown too quickly
      await new Promise((resolve) => setTimeout(resolve, 1000))

      console.error('Failed to save template:', error)
      // Show error state temporarily
      saveError.value = true
      setTimeout(() => {
        saveError.value = false
      }, 2500)
    } finally {
      // Ensure loading state is visible for at least 1 second
      setTimeout(() => {
        isSaving.value = false
      }, 500)
    }
  }

  // Delete template confirmation
  const showDeleteModal = ref(false)
  const templateToDelete = ref<AiChatQuestionTemplate | null>(null)

  const confirmDeleteTemplate = (template: AiChatQuestionTemplate) => {
    templateToDelete.value = template
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const deleteTemplate = async () => {
    if (templateToDelete.value) {
      try {
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesDestroy({
          uuid: templateToDelete.value.uuid
        })
        await fetchTemplates()
        closeDeleteModal()
      } catch (error) {
        console.error('Failed to delete template:', error)
      }
    }
  }
</script>

<template>
  <div
    class="flex flex-col rounded-lg bg-white"
    data-cy="prompt-templates-modal"
  >
    <!-- Header with close button -->
    <div
      class="flex items-center justify-between border-b border-gray-200 px-4 py-3"
      data-cy="prompt-templates-modal-header"
    >
      <h2
        class="text-lg font-medium"
        data-cy="prompt-templates-modal-title"
      >
        {{ t('title') }}
      </h2>
    </div>

    <!-- Template Grid View -->
    <div
      v-if="currentView === 'grid'"
      class="flex max-h-[80vh] min-h-[80vh] overflow-hidden"
    >
      <!-- Sidebar with categories -->
      <div
        class="w-64 flex-shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50"
        data-cy="prompt-templates-sidebar"
      >
        <!-- Categories list -->
        <div
          class="space-y-1 p-4"
          data-cy="prompt-templates-categories"
        >
          <!-- All templates section -->
          <button
            class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
              transition-colors"
            :class="
              activeCategory === 'all'
                ? 'bg-purple-100 text-purple-900'
                : 'text-gray-700 hover:bg-gray-100'
            "
            data-cy="prompt-templates-category-all"
            @click="setActiveCategory('all')"
          >
            <span>{{ t('all') }}</span>
            <span class="text-xs text-gray-400">{{ specialCategoryCounts.all }}</span>
          </button>

          <!-- Favorites section -->
          <button
            class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
              transition-colors"
            :class="
              activeCategory === 'favorites'
                ? 'bg-purple-100 text-purple-900'
                : 'text-gray-700 hover:bg-gray-100'
            "
            data-cy="prompt-templates-category-favorites"
            @click="setActiveCategory('favorites')"
          >
            <span>{{ t('favorites') }}</span>
            <span class="text-xs text-gray-400">{{ specialCategoryCounts.favorites }}</span>
          </button>

          <!-- Jupus section -->
          <button
            class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
              transition-colors"
            :class="
              activeCategory === 'jupus'
                ? 'bg-purple-100 text-purple-900'
                : 'text-gray-700 hover:bg-gray-100'
            "
            data-cy="prompt-templates-category-jupus"
            @click="setActiveCategory('jupus')"
          >
            <span>{{ t('jupus') }}</span>
            <span class="text-xs text-gray-400">{{ specialCategoryCounts.jupus }}</span>
          </button>

          <!-- Saved section -->
          <button
            class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
              transition-colors"
            :class="
              activeCategory === 'saved'
                ? 'bg-purple-100 text-purple-900'
                : 'text-gray-700 hover:bg-gray-100'
            "
            data-cy="prompt-templates-category-saved"
            @click="setActiveCategory('saved')"
          >
            <span>{{ t('saved') }}</span>
            <span class="text-xs text-gray-400">{{ specialCategoryCounts.saved }}</span>
          </button>

          <!-- Horizontal divider -->
          <div class="my-4 border-t border-gray-200"></div>

          <!-- Dynamically generated law field categories -->
          <button
            v-for="category in lawFieldCategories"
            :key="category.name"
            class="mb-4 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium
              transition-colors"
            :class="
              activeCategory === category.name
                ? 'bg-purple-100 text-purple-900'
                : 'text-gray-700 hover:bg-gray-100'
            "
            @click="setActiveCategory(category.name)"
          >
            <span>{{ category.name }}</span>
            <span class="text-xs text-gray-400">{{ category.count }}</span>
          </button>
        </div>
      </div>

      <!-- Templates area -->
      <div
        class="flex flex-1 flex-col"
        data-cy="prompt-templates-content"
      >
        <!-- Header with title, search and new template -->
        <div
          class="flex items-center justify-between px-4 py-3"
          data-cy="prompt-templates-header"
        >
          <h2
            class="text-lg font-medium"
            data-cy="prompt-templates-content-title"
          >
            {{
              activeCategory === 'all'
                ? t('all')
                : activeCategory === 'favorites'
                  ? t('favorites')
                  : activeCategory === 'jupus'
                    ? t('jupus')
                    : activeCategory === 'saved'
                      ? t('saved')
                      : activeCategory
            }}
          </h2>
          <div
            class="flex items-center space-x-2"
            data-cy="prompt-templates-actions"
          >
            <div
              class="relative w-64"
              data-cy="prompt-templates-search"
            >
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search class="h-4 w-4 text-gray-400" />
              </div>
              <Input
                v-model="searchQuery"
                class="pl-10"
                :placeholder="t('search')"
                data-cy="prompt-templates-search-input"
              />
            </div>
            <button
              class="inline-flex items-center gap-x-1.5 rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold
                text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2
                focus-visible:outline-offset-2 focus-visible:outline-purple-600"
              data-cy="prompt-templates-new-template-button"
              @click="createNewTemplate"
            >
              <Plus class="-ml-0.5 h-4 w-4" />
              {{ t('newTemplate') }}
            </button>
          </div>
        </div>

        <!-- Templates grid -->
        <div
          class="flex-1 overflow-y-auto p-4"
          data-cy="prompt-templates-grid"
        >
          <div
            class="grid grid-cols-2 gap-4"
            data-cy="prompt-templates-grid-container"
          >
            <div
              v-for="template in filteredTemplates"
              :key="template.uuid"
              class="flex cursor-pointer flex-col rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow
                hover:shadow-md"
              data-cy="prompt-template-card"
            >
              <!-- Template header with title and actions -->
              <div class="flex items-center justify-between p-4">
                <div class="flex items-center">
                  <div class="text-base font-medium">
                    {{ template.title }}
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <TooltipProvider v-if="template.is_jupus_template">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <div
                          class="cursor-help rounded-sm bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700"
                        >
                          {{ t('jupus') }}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent class="max-w-xs">
                        <p>{{ t('jupusTemplateTooltip') }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <button
                    class="rounded-md p-1.5 hover:bg-gray-100"
                    :class="
                      template.is_favorite
                        ? 'text-purple-600'
                        : 'text-gray-400 hover:text-purple-500'
                    "
                    data-cy="prompt-template-favorite-button"
                    @click.stop="toggleFavorite(template)"
                  >
                    <Star
                      class="h-5 w-5"
                      :fill="template.is_favorite ? 'currentColor' : 'none'"
                    />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                      <Button
                        variant="outline"
                        class="flex h-8 rounded border border-gray-200 px-2 data-[state=open]:bg-muted"
                        @click.stop
                      >
                        <MoreVertical class="h-4 w-4" />
                        <span class="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      class="w-[160px]"
                    >
                      <DropdownMenuItem
                        v-if="!template.is_jupus_template"
                        class="cursor-pointer"
                        @click.stop="editTemplate(template)"
                      >
                        <Pencil class="mr-2 h-4 w-4" />
                        {{ t('edit') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        class="cursor-pointer"
                        @click.stop="duplicateTemplate(template)"
                      >
                        <Copy class="mr-2 h-4 w-4" />
                        {{ t('duplicate') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        v-if="!template.is_jupus_template"
                        class="cursor-pointer text-red-600"
                        @click.stop="confirmDeleteTemplate(template)"
                      >
                        <Trash class="mr-2 h-4 w-4" />
                        {{ t('delete') }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <!-- Template content -->
              <div class="flex-1 px-4 pb-4">
                <div class="line-clamp-3 text-sm text-gray-600">
                  {{
                    template.description
                      ? getPromptPreview(template.description)
                      : getPromptPreview(template.prompt)
                  }}
                </div>
              </div>

              <!-- Template footer with category and use button -->
              <div class="flex items-center justify-between px-4 py-3">
                <div class="flex items-center">
                  <div
                    class="flex items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium"
                    :class="template.law_field ? 'text-black' : 'text-gray-400'"
                  >
                    <Scale class="mr-1.5 h-4 w-4" />
                    <span
                      v-if="template.law_field"
                      class="max-w-[200px] truncate"
                    >
                      {{ translateLawField(template.law_field)
                      }}{{ template.law_topic ? '/' + translateLawTopic(template.law_topic) : '' }}
                    </span>
                    <span v-else>{{ t('noLawFieldSelected') }}</span>
                  </div>
                </div>
                <button
                  class="rounded-md bg-purple-600 px-3 py-1 text-sm font-medium text-white shadow-sm hover:bg-purple-500
                    focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-purple-600"
                  data-cy="prompt-template-use-button"
                  @click.stop="selectTemplate(template)"
                >
                  {{ t('use') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Show message if no templates available -->
          <div
            v-if="filteredTemplates.length === 0"
            class="p-4 text-center text-gray-500"
          >
            {{ t('noTemplates') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Template Form View -->
    <div
      v-if="currentView === 'form'"
      class="max-h-[80vh] min-h-[80vh] overflow-y-auto p-6"
    >
      <div class="mx-auto max-w-xl">
        <!-- Back to templates link -->
        <div class="mb-5">
          <button
            class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
            @click="closeTemplateForm"
          >
            <ChevronLeft class="mr-1 h-4 w-4" />
            {{ t('backToAllTemplates') }}
          </button>
        </div>

        <!-- Title header and save button on same axis -->
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xl font-medium">
            {{ templateTitle || t('untitledTemplate') }}
          </h2>

          <!-- Save button with different states -->
          <Button
            variant="default"
            :disabled="isSaving || !isFormValid"
            :class="{
              'rounded-md px-4 py-2 text-sm font-medium': true,
              'bg-purple-600 text-white hover:bg-purple-700': !saveSuccess && !saveError,
              'bg-green-600 text-white': saveSuccess,
              'bg-red-600 text-white': saveError
            }"
            data-cy="template-save-button"
            @click="saveTemplate"
          >
            <!-- Loading state -->
            <LoaderCircle
              v-if="isSaving"
              class="mr-2 h-4 w-4 animate-spin"
            />

            <!-- Success state -->
            <Check
              v-if="saveSuccess && !isSaving"
              class="mr-2 h-4 w-4"
            />

            <!-- Error state -->
            <AlertCircle
              v-if="saveError && !isSaving"
              class="mr-2 h-4 w-4"
            />

            {{ saveSuccess ? t('saved') : saveError ? t('saveFailed') : t('saveChanges') }}
          </Button>
        </div>

        <!-- Form Content Box -->
        <div class="rounded-lg border border-gray-200 bg-white shadow-sm">
          <!-- Form Fields -->
          <div class="px-6 py-4">
            <!-- Template Title -->
            <div class="mb-4">
              <label
                for="templateTitle"
                class="mb-1 block text-sm font-medium"
              >
                {{ t('templateTitle') }}
              </label>
              <InputField
                v-model="templateTitle"
                name="templateTitle"
                :maxlength="TEMPLATE_TITLE_MAX_LENGTH"
                :disabled="isSaving"
                :placeholder="t('templateTitlePlaceholder')"
                :data-cy="'template-title-input'"
              />
              <div
                class="mt-0.5 flex justify-end text-xs"
                :class="isTemplateTitleValid ? 'text-green-500' : 'text-red-500'"
              >
                {{ templateTitle.length }}/{{ TEMPLATE_TITLE_MAX_LENGTH }} {{ t('characters') }}
              </div>
            </div>

            <!-- Law Field and Topic -->
            <div class="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label
                  for="lawField"
                  class="mb-1 block text-sm font-medium"
                >
                  {{ t('lawField') }}
                </label>
                <SelectField
                  v-model="selectedLawField"
                  name="lawField"
                  :options="lawFieldOptions"
                  :disabled="isSaving"
                  :placeholder="t('selectLawField')"
                  @update:model-value="handleLawFieldChange"
                />
              </div>
              <div>
                <label
                  for="lawTopic"
                  class="mb-1 block text-sm font-medium"
                >
                  {{ t('lawTopic') }}
                </label>
                <SelectField
                  v-model="selectedLawTopic"
                  name="lawTopic"
                  :options="lawTopicOptions"
                  :disabled="isSaving || !selectedLawField"
                  :placeholder="t('selectLawTopic')"
                />
              </div>
            </div>

            <!-- Prompt -->
            <div class="mb-3">
              <label
                for="prompt"
                class="mb-1 block text-sm font-medium"
              >
                {{ t('prompt') }}
              </label>
              <TextAreaField
                v-model="promptText"
                name="prompt"
                rows="5"
                :placeholder="t('promptPlaceholder')"
                :disabled="isSaving"
                :error="!isPromptValid && promptText !== '' ? t('promptRequired') : ''"
                :data-cy="'template-prompt-input'"
              />
            </div>

            <!-- Description -->
            <div class="mb-3">
              <div class="mb-1 flex items-center justify-between">
                <label
                  for="description"
                  class="block text-sm font-medium"
                >
                  {{ t('description') }}
                </label>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-xs font-medium
                    text-purple-700 hover:bg-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isSaving || isGeneratingDescription || !promptText"
                  @click="generateDescription"
                >
                  <span
                    v-if="isGeneratingDescription"
                    class="animate-spin"
                  >
                    <LoaderCircle class="h-3 w-3" />
                  </span>
                  <span v-else>
                    <RotateCw class="h-3 w-3" />
                  </span>
                  {{ isGeneratingDescription ? t('generatingDescription') : t('generateWithAI') }}
                </button>
              </div>
              <TextAreaField
                v-model="descriptionText"
                name="description"
                rows="3"
                :placeholder="t('descriptionPlaceholder')"
                :disabled="isSaving || isGeneratingDescription"
                maxlength="300"
                :error="
                  descriptionText.length > DESCRIPTION_MAX_LENGTH ? t('descriptionTooLong') : ''
                "
                :data-cy="'template-description-input'"
              />
              <div
                class="mt-1 text-right text-xs text-gray-500"
                :class="!isDescriptionValid ? 'text-red-500' : 'text-green-500'"
              >
                {{ descriptionText.length }}/{{ DESCRIPTION_MAX_LENGTH }} {{ t('characters') }}
              </div>
            </div>

            <!-- Folder Assignment -->
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">{{ t('folders') }}</label>
              <div
                v-if="globalChatStore.sessionGroups.length > 0"
                class="flex flex-wrap gap-2"
              >
                <label
                  v-for="folder in globalChatStore.sessionGroups"
                  :key="folder.uuid"
                  class="flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-xs
                    font-medium transition-colors"
                  :class="
                    selectedFolderUuids.includes(folder.uuid)
                      ? 'border-purple-300 bg-purple-50 text-purple-700'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  "
                >
                  <input
                    type="checkbox"
                    class="sr-only"
                    :value="folder.uuid"
                    :checked="selectedFolderUuids.includes(folder.uuid)"
                    :disabled="isSaving"
                    @change="
                      selectedFolderUuids.includes(folder.uuid)
                        ? (selectedFolderUuids = selectedFolderUuids.filter(
                            (u) => u !== folder.uuid
                          ))
                        : selectedFolderUuids.push(folder.uuid)
                    "
                  />
                  {{ folder.name }}
                </label>
              </div>
              <p
                v-else
                class="text-xs text-gray-400"
              >
                {{ t('noFolders') }}
              </p>
            </div>

            <!-- Template Visibility Note -->
            <p class="text-xs text-gray-500">
              {{ t('templateVisibility') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete confirmation dialog -->
  <Dialog v-model:open="showDeleteModal">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{{ t('confirmDelete') }}</DialogTitle>
        <DialogDescription>
          {{ t('deleteTemplateQuestion') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="outline"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="destructive"
          @click="deleteTemplate"
        >
          {{ t('delete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Template form dialog has been integrated directly into the main dialog -->
</template>

<style scoped>
  .rotate-180 {
    transform: rotate(180deg);
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
